import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Building2,
  Settings,
  Archive,
  Trash2,
  MoreVertical,
  AlertCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { fundService } from '../../services/fundService';
import { FundSummary } from '../../types/fund';
import { useToast } from '../../hooks/useToast';
import { useFunds } from '@/hooks/react-query/useFunds';
import { useAllFundsCommitmentTotals } from '@/hooks/react-query/useInvestors';
import { queryKeys } from '@/hooks/react-query/queryKeys';
import {
  formatCurrency,
  formatDate,
  safeParseNumber,
} from '@/utils/formatters';

export function FundManagement() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedFund, setSelectedFund] = useState<FundSummary | null>(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [archiveConfirmation, setArchiveConfirmation] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: commitmentTotals = {} } = useAllFundsCommitmentTotals();
  const { data: funds = [], isLoading: loading } = useFunds({
    include_archived: includeArchived,
  });

  const refetchFunds = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.funds.all });

  const handleArchiveFund = async () => {
    if (!selectedFund) return;

    try {
      setActionLoading(true);
      if (selectedFund.is_archived) {
        await fundService.unarchiveFund(selectedFund.id);
        toast({
          title: 'Success',
          description: `${selectedFund.name} has been unarchived`,
        });
      } else {
        await fundService.archiveFund(selectedFund.id);
        toast({
          title: 'Success',
          description: `${selectedFund.name} has been archived`,
        });
      }
      setShowArchiveDialog(false);
      setSelectedFund(null);
      setArchiveConfirmation('');
      await refetchFunds();
    } catch (error) {
      console.error('Failed to archive/unarchive fund:', error);
      toast({
        title: 'Error',
        description: 'Failed to update fund archive status',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteFund = async () => {
    if (!selectedFund) return;

    try {
      setActionLoading(true);
      await fundService.deleteFund(selectedFund.id);
      toast({
        title: 'Success',
        description: `${selectedFund.name} has been permanently deleted`,
      });
      setShowDeleteDialog(false);
      setSelectedFund(null);
      setDeleteConfirmation('');
      await refetchFunds();
    } catch (error: unknown) {
      console.error('Failed to delete fund:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete fund';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<
      string,
      {
        variant:
          'default' | 'secondary' | 'success' | 'warning' | 'destructive';
        label: string;
      }
    > = {
      setup: { variant: 'secondary', label: 'Setup' },
      fundraising: { variant: 'warning', label: 'Fundraising' },
      investing: { variant: 'success', label: 'Investing' },
      harvesting: { variant: 'success', label: 'Harvesting' },
      liquidated: { variant: 'default', label: 'Liquidated' },
    };

    const config = statusVariants[status] || {
      variant: 'default',
      label: status,
    };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading funds...</p>
        </div>
      </div>
    );
  }

  const archiveAction = selectedFund?.is_archived ? 'Unarchive' : 'Archive';
  const archiveButtonLabel = actionLoading ? 'Processing...' : archiveAction;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Fund Management
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage fund settings, archiving, and deletion
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIncludeArchived(!includeArchived)}
        >
          {includeArchived ? (
            <>
              <EyeOff className="h-4 w-4 mr-2" />
              Hide Archived
            </>
          ) : (
            <>
              <Eye className="h-4 w-4 mr-2" />
              Show Archived
            </>
          )}
        </Button>
      </div>
      <div className="space-y-4">
        {funds.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No funds found</div>
        ) : (
          funds.map((fund) => (
            <Card
              key={fund.id}
              className={fund.is_archived ? 'opacity-60' : ''}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold">{fund.name}</h3>
                      {fund.is_archived && (
                        <Badge variant="secondary">Archived</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {fund.fund_type
                        ? fund.fund_type.charAt(0).toUpperCase() +
                          fund.fund_type.slice(1)
                        : 'Unknown'}{' '}
                      Fund •{fund.base_currency || 'N/A'} • Vintage{' '}
                      {fund.vintage_year || 'N/A'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(fund.status)}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Settings className="h-4 w-4 mr-2" />
                          Fund Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedFund(fund);
                            setShowArchiveDialog(true);
                          }}
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          {fund.is_archived ? 'Unarchive' : 'Archive'} Fund
                        </DropdownMenuItem>
                        {fund.is_archived && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedFund(fund);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Fund
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Commitment</p>
                    <p className="font-medium">
                      {formatCurrency(
                        safeParseNumber(commitmentTotals[fund.id] ?? '0'),
                        { currency: fund.base_currency }
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-medium">{formatDate(fund.created_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={showArchiveDialog}
        onOpenChange={(open) => {
          setShowArchiveDialog(open);
          if (!open) {
            setArchiveConfirmation('');
            setSelectedFund(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedFund?.is_archived ? 'Unarchive' : 'Archive'} Fund
            </DialogTitle>
            <DialogDescription>
              {selectedFund?.is_archived ? (
                <>
                  Are you sure you want to unarchive{' '}
                  <strong>{selectedFund?.name}</strong>? The fund will become
                  visible again in all interfaces.
                </>
              ) : (
                <>
                  Are you sure you want to archive{' '}
                  <strong>{selectedFund?.name}</strong>? The fund will be hidden
                  from all interfaces but can be restored later.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label htmlFor="archive-confirmation">
              Type <strong>{selectedFund?.name}</strong> to confirm
            </Label>
            <Input
              id="archive-confirmation"
              value={archiveConfirmation}
              onChange={(e) => setArchiveConfirmation(e.target.value)}
              placeholder="Type fund name to confirm"
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowArchiveDialog(false);
                setSelectedFund(null);
                setArchiveConfirmation('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchiveFund}
              disabled={
                actionLoading || archiveConfirmation !== selectedFund?.name
              }
            >
              {archiveButtonLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) {
            setDeleteConfirmation('');
            setSelectedFund(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Fund</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete{' '}
              <strong>{selectedFund?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This action cannot be undone. All data associated with this fund
              will be permanently deleted, including investors, transactions,
              documents, and financial records.
            </AlertDescription>
          </Alert>

          <div className="py-4">
            <Label htmlFor="delete-confirmation">
              Type <strong>{selectedFund?.name}</strong> to confirm deletion
            </Label>
            <Input
              id="delete-confirmation"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Type fund name to confirm"
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedFund(null);
                setDeleteConfirmation('');
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFund}
              disabled={
                actionLoading || deleteConfirmation !== selectedFund?.name
              }
            >
              {actionLoading ? 'Deleting...' : 'Delete Fund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
